import type {
  ActivitySnapshot,
  PhaseProgressUpdate,
  WaitDescriptor,
} from '@volley/crucible-ipc-types/ipc-channels';
import type {
  AutoBuildState,
  BuildGameInterviewState,
  BuildGameInterviewStatus,
  KnownInterviewDepth,
  KnownInterviewSubPhase,
  PhaseBDecision,
} from '@volley/crucible-ipc-types/build-game-types';
import type { FoundryGameStateSnapshot } from '@volley/crucible-build-state/state-kernel.js';
import { hasVerifiedBuildCompletionEvidence } from '@volley/crucible-build-state/build-completion-evidence.js';
import {
  getEffectiveInterviewDepth,
  getEffectiveInterviewSubPhase,
  getEffectivePlan,
  isKnownBuildGameInterviewStatus,
} from '@volley/crucible-ipc-types/build-game-types';
import { NOISY_STUCK_THRESHOLD_MS } from '@volley/crucible-ipc-types/build-game-constants';
import { savedPlanMatchesBuilt } from '../components/plan-state';
import { pickOldestWait } from '../components/ActivityBanner';
import { formatWaitLabel } from '../wait-label';
import { planRevisionLockedForHandoff } from './plan-build-handoff';
import { isGuidedPlanAwaitingApproval } from '@volley/crucible/interview/guided-plan-approval.js';

export type WorkspaceRunKind =
  | 'ready'
  | 'starting-interview'
  | 'interview-in-progress'
  | 'drafting-plan'
  | 'waiting-for-plan-review'
  | 'build-kickoff-pending'
  | 'building'
  | 'waiting-on-user'
  | 'done'
  | 'needs-attention';

export type WorkspaceRunTone = 'neutral' | 'active' | 'waiting' | 'success' | 'warning' | 'error';

export type WorkspaceRunPrimaryCta =
  | 'start-interview'
  | 'review-plan'
  | 'start-building'
  | 'back-to-plan'
  | 'retry';

export interface WorkspaceRunState {
  kind: WorkspaceRunKind;
  label: string;
  detail?: string;
  tone: WorkspaceRunTone;
  /** Renderer-only compatibility marker; does not grant verified build completion. */
  legacyCompletionUnverified?: true;
  primaryCta?: WorkspaceRunPrimaryCta;
  showBackToPlan: boolean;
  /** Plan markdown edits locked after approval / during build (handoff spec). */
  planRevisionLocked: boolean;
  disableComposerReason?: string;
  elapsedSince?: number | null;
  debugSource: string;
}

export type InitialKickoffStatus =
  | 'idle'
  | 'waiting-for-renderer-ready'
  | 'dispatching'
  | 'dispatched'
  | 'timed-out'
  | 'failed';

export type InitialKickoffFailureReason =
  | 'renderer-not-ready-timeout'
  | 'agent-send-rejected'
  | 'interview-state-missing'
  | 'anthropic-token-missing'
  | 'cloud-proxy-missing'
  | 'turn-in-flight';

export interface InitialKickoffState {
  status: InitialKickoffStatus;
  reason?: InitialKickoffFailureReason;
  detail?: string;
  startedAt?: number | null;
}

export interface BuildKickoffWatchdogState {
  status: 'idle' | 'pending' | 'stalled' | 'resume-attempted';
  committedAt?: number;
  stalledAt?: number;
}

export interface WorkspaceRunSignals {
  activitySnapshot: ActivitySnapshot | null;
  interviewState: BuildGameInterviewState | null | undefined;
  interviewStatus: BuildGameInterviewStatus | null | undefined;
  /** Last durable interview progress timestamp from the status summary. */
  interviewProgressAt?: string | null;
  /** Wall-clock reading used only for interview-progress staleness. */
  interviewNowMs?: number;
  phaseProgress: PhaseProgressUpdate | null | undefined;
  /**
   * #3033/#3034 — POSITIVE proof-of-life from the SINGLE build-watch liveness
   * source (`useActivityLiveness`, client-relative + skew-immune). This is the
   * ONLY thing that suppresses the stale-build / stale-final-review escalations.
   * It is TRUE only after a client-OBSERVED advance while fresh/quiet — NOT on a
   * warm-up `fresh` (first-seen stamp, which could be an old pod value from a
   * build that stalled before mount) and NOT while stalled/unknown. So a
   * genuinely-dead build mounted-into does not mask the backstops: the
   * 15-minute wall-clock and the #2917 kernel idle-heartbeat escalations fire on
   * their own criteria (#3033 round-2). Absent ⇒ treated as not-proven ⇒ the
   * detector stays armed.
   */
  provenLive?: boolean;
  foundrySnapshot?: FoundryGameStateSnapshot | null;
  isAiTyping: boolean;
  chatPersistError?: Error | { message?: string } | null;
  planModeError?: string | null;
  initialKickoff?: InitialKickoffState | null;
  isInterviewWaitingForUser?: boolean;
  /** The adaptive intake authority proved that no live adaptive session or checkpoint exists. */
  intakeAuthorityMissing?: boolean;
  buildKickoffWatchdog?: BuildKickoffWatchdogState;
  fastAnswersSubmittedAt?: number | null;
  fastPlanMissingAt?: number | null;
  /**
   * crucible#3183 — when set, the design-agent reached a TERMINAL 'failed'
   * status (a `create:build-step` event with `step:'design-system',
   * status:'failed'` — see Workspace.tsx's `designSystemWarning` tracker,
   * added for crucible#2756) and no build plan is coming from it. Before this,
   * the plan-phase branches below had no way to learn this and showed
   * "drafting plan" forever: `seedDesignBuildPlanState` (which writes the
   * plan) never runs when design-system generation throws — by design, see
   * `seed-build-from-design.ts`'s "design system is the spine, no silent
   * fallback" docblock — so a design-system failure with `!hasPlan` was
   * indistinguishable from "still generating" with no backstop, unlike
   * `fastPlanMissingAt`'s sibling case a few lines below. Observed live
   * (2026-07-15): a real-spend run hung 50+ minutes on exactly this.
   */
  designSystemFailedAt?: number | null;
  autoBuildState?: AutoBuildState | null;
  /**
   * Escalation-clock reading for the 15-minute stale-BUILD wall-clock check
   * (`nowMs - phaseStart >= STALE_BUILD_NO_LIVE_AGENT_MS`). This is a DIFFERENT
   * signal from `liveness` (it ages phase-start, not `lastActivityAt`), left
   * intact by #3033/#3034. Supplied by `useActivityLiveness` (same single
   * clock). Absent ⇒ the stale-build escalation cannot fire.
   */
  nowMs?: number;
  /**
   * #3035 — CLIENT-RELATIVE phase-start anchor for the snapshot-only
   * implementation-phase stale-build detector. The Foundry snapshot carries NO
   * phase-start timestamp, so `useActivityLiveness` stamps WHEN the snapshot
   * phase (`phase.current`) was first OBSERVED on the CLIENT clock, per build
   * epoch (analogous to its `lastActivityAt` memo). `staleBuildAttentionFromKernel`
   * ages this against `nowMs` for the 15-minute window on the DEGRADED path
   * (phase-progress subscription undefined). Both are the same client clock, so
   * `nowMs - snapshotPhaseStartedAt` is skew-immune. Absent ⇒ no anchor ⇒ the
   * snapshot stale-build escalation cannot fire.
   */
  snapshotPhaseStartedAt?: number | null;
}

interface InterviewSnapshot {
  exists: boolean;
  depth: KnownInterviewDepth | undefined;
  subPhase: KnownInterviewSubPhase | undefined;
  plan: string;
  status: BuildGameInterviewStatus | null;
  legacyCompletionUnverified: boolean;
  autoBuild: AutoBuildState | null;
  planRevision?: number;
  planHash?: number;
  lastBuiltPlanRevision?: number;
  lastBuiltPlanHash?: number;
  dispatchedAt?: string | null;
  buildDispatchState?: 'approved' | 'accepted' | 'failed';
  pendingPhaseBDecision: {
    category?: string;
    askedAt?: string;
    optionIds?: string[];
    optionLabels?: Record<string, string>;
  } | null;
}

const TERMINAL_STATUSES = new Set<BuildGameInterviewStatus>(['built', 'built-partial']);

const LEGACY_UNVERIFIED_COMPLETION_STATUS = 'legacy-completion-unverified';

const ATTENTION_STATUSES = new Set<BuildGameInterviewStatus>(['abandoned']);

const STALE_FINAL_REVIEW_DETAIL =
  'The build stopped before finalizing. Review the game, then resume or finalize the build.';
const STALE_BUILD_NO_LIVE_AGENT_MS = 15 * 60 * 1000;
const PHASE_PROGRESS_FUTURE_SKEW_MS = 60 * 1000;
const STALE_BUILD_DETAIL =
  'No build agents have checked in for this phase for a while. Resume the build or go back to the plan.';
const UNVERIFIED_BUILD_COMPLETION_DETAIL =
  'Rebuild or retry so Foundry can verify the current build before play or deploy.';

function staleInterviewProgressAt(
  progressAt: string | null | undefined,
  nowMs: number | undefined,
): number | null {
  if (!progressAt || nowMs === undefined) return null;
  const parsed = Date.parse(progressAt);
  if (!Number.isFinite(parsed) || nowMs - parsed < NOISY_STUCK_THRESHOLD_MS) return null;
  return parsed;
}

export function isCompletionVerificationRetryRunState(
  runState: Pick<WorkspaceRunState, 'kind' | 'primaryCta' | 'debugSource'> | null | undefined,
): boolean {
  if (runState?.kind !== 'needs-attention' || runState.primaryCta !== 'retry') return false;
  return (
    runState.debugSource.includes('completion-unverified') ||
    runState.debugSource.endsWith(':unverified')
  );
}

function interviewSnapshot(
  state: BuildGameInterviewState | null | undefined,
  statusSummary: BuildGameInterviewStatus | null | undefined,
): InterviewSnapshot {
  const depth = state ? getEffectiveInterviewDepth(state) : undefined;
  const subPhase = state ? getEffectiveInterviewSubPhase(state) : undefined;
  const plan = state ? getEffectivePlan(state) : '';
  const hasLegacyCompletionProjection = state?.status === LEGACY_UNVERIFIED_COMPLETION_STATUS;
  const activeLegacySummary =
    hasLegacyCompletionProjection &&
    (statusSummary === 'dispatched' || statusSummary === 'building')
      ? statusSummary
      : null;
  const legacyCompletionUnverified = hasLegacyCompletionProjection;
  const status = activeLegacySummary
    ? activeLegacySummary
    : hasLegacyCompletionProjection
      ? null
      : state?.status && isKnownBuildGameInterviewStatus(state.status)
        ? state.status
        : (statusSummary ?? null);
  const autoBuild = state?.autoBuild ?? null;
  const planRevision = state?.planRevision;
  const planHash = state?.planHash;
  const lastBuiltPlanRevision = state?.lastBuiltPlanRevision;
  const lastBuiltPlanHash = state?.lastBuiltPlanHash;
  const dispatchedAt = state?.dispatchedAt;
  const buildDispatchState = state?.buildDispatch?.state;
  const pendingPhaseBDecision =
    state?.phaseBDecisions?.find(
      (decision) =>
        decision.actionStatus === 'pending' &&
        (decision.answerOptionId === null || decision.answerOptionId === undefined),
    ) ?? null;
  return {
    exists: state != null || statusSummary != null,
    depth,
    subPhase,
    plan,
    status,
    legacyCompletionUnverified,
    autoBuild,
    planRevision,
    planHash,
    lastBuiltPlanRevision,
    lastBuiltPlanHash,
    dispatchedAt,
    buildDispatchState,
    pendingPhaseBDecision,
  };
}

function hasRunningActivity(snapshot: ActivitySnapshot | null): boolean {
  if (snapshot === null) return false;
  return Object.values(snapshot.byAgentId).some((row) => row.status === 'running');
}

function hasFreshRunningActivity(snapshot: ActivitySnapshot | null): boolean {
  if (snapshot === null) return false;
  return Object.values(snapshot.byAgentId).some((row) => row.status === 'running' && !row.stale);
}

function hasFreshBuildAgentActivity(snapshot: ActivitySnapshot | null): boolean {
  if (snapshot === null || snapshot.heartbeatState === 'waiting') return false;
  return Object.values(snapshot.byAgentId).some(
    (row) => row.status === 'running' && !row.stale && row.agentId !== '__main__',
  );
}

function runningStartedAt(snapshot: ActivitySnapshot | null): number | null {
  if (snapshot === null) return null;
  const rows = Object.values(snapshot.byAgentId).filter((row) => row.status === 'running');
  if (rows.length === 0) return null;
  return Math.min(...rows.map((row) => row.startedAt));
}

function phaseProgressBeyondPlanReview(progress: PhaseProgressUpdate | null | undefined): boolean {
  return progress?.kind === 'derived' && progress.derived.current > 2;
}

function phaseProgressAtFinalReview(progress: PhaseProgressUpdate | null | undefined): boolean {
  return progress?.kind === 'derived' && progress.derived.current === 6;
}

function snapshotIsImplicitPlanReview(snapshot: FoundryGameStateSnapshot | null | undefined) {
  return (
    snapshot?.build.status === 'running' &&
    snapshot.build.terminal === false &&
    snapshot.pendingAction.kind !== 'plan-review' &&
    snapshot.phase.current === 2
  );
}

function phaseProgressBlocked(progress: PhaseProgressUpdate | null | undefined): boolean {
  return progress?.kind === 'derived' && progress.derived.blockedBy !== undefined;
}

function isTerminalInterviewStatus(status: BuildGameInterviewStatus | null | undefined): boolean {
  return status !== null && status !== undefined && TERMINAL_STATUSES.has(status);
}

function hasSubmittedValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return typeof value === 'number' || typeof value === 'boolean';
}

function answerableQuestionValues(
  value: unknown,
): Array<{ answer?: unknown; skippedAt?: unknown }> {
  const candidates = Array.isArray(value)
    ? value
    : value && typeof value === 'object'
      ? Object.values(value)
      : [];
  return candidates.filter(
    (candidate): candidate is { answer?: unknown; skippedAt?: unknown } =>
      candidate !== null && typeof candidate === 'object',
  );
}

function fastAnswersSubmitted(state: BuildGameInterviewState | null | undefined): boolean {
  if (!state) return false;
  const quickAnswers = state.quickAnswers;
  if (quickAnswers && typeof quickAnswers === 'object') {
    const values = Object.values(quickAnswers);
    if (values.length >= 5 && values.every(hasSubmittedValue)) return true;
  }

  const blocks = state.agents ? Object.values(state.agents) : [];
  let total = 0;
  for (const block of blocks) {
    const questions = answerableQuestionValues(block.questions);
    for (const question of questions) {
      total += 1;
      if (!hasSubmittedValue(question.answer) && !hasSubmittedValue(question.skippedAt)) {
        return false;
      }
    }
  }
  return total >= 5;
}

export function shouldSurfacePendingPhaseBDecision(args: {
  activitySnapshot: ActivitySnapshot | null;
  phaseProgress: PhaseProgressUpdate | null | undefined;
}): boolean {
  if (phaseProgressBeyondPlanReview(args.phaseProgress)) return false;
  return !(
    hasFreshRunningActivity(args.activitySnapshot) &&
    args.activitySnapshot?.heartbeatState !== 'waiting'
  );
}

/**
 * Resolve whether the Phase B decision card should render in chat.
 *
 * When the kernel snapshot reports `pendingAction.kind === 'phase-b-decision'`,
 * trust that durable signal and skip legacy activity / phase-progress gating so
 * the card stays aligned with `deriveWorkspaceRunStateFromFoundrySnapshot`.
 *
 * Args:
 *   args: Kernel snapshot, extracted interview decision, and legacy signals.
 *
 * Returns:
 *   The pending decision to surface, or null when the card should stay hidden.
 */
export function resolvePendingPhaseBDecision(args: {
  foundrySnapshot: FoundryGameStateSnapshot | null | undefined;
  decision: PhaseBDecision | null;
  activitySnapshot: ActivitySnapshot | null;
  phaseProgress: PhaseProgressUpdate | null | undefined;
}): PhaseBDecision | null {
  if (args.foundrySnapshot?.build.terminal) return null;
  if (args.foundrySnapshot && args.foundrySnapshot.pendingAction.kind !== 'phase-b-decision') {
    return null;
  }
  if (!args.decision) return null;
  if (args.foundrySnapshot?.pendingAction.kind === 'phase-b-decision') {
    return args.decision;
  }
  return shouldSurfacePendingPhaseBDecision({
    activitySnapshot: args.activitySnapshot,
    phaseProgress: args.phaseProgress,
  })
    ? args.decision
    : null;
}

function phaseProgressStartedAt(progress: PhaseProgressUpdate | null | undefined): number | null {
  if (progress?.kind !== 'derived') return null;
  const current = progress.derived.current;
  const transition = [...progress.derived.transitions]
    .reverse()
    .find((entry) => entry.phase === current);
  return parseIsoTimeMs(transition?.at);
}

const ULID_ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const ULID_LENGTH = 26;
const ULID_TIME_LENGTH = 10;
const ULID_MAX_TIME = 281_474_976_710_655;

/** Decode the durable millisecond timestamp carried by production reviewer ULIDs. */
function reviewerEmissionTimeMs(emissionId: string): number | null {
  if (emissionId.length !== ULID_LENGTH) return null;
  const normalized = emissionId.toUpperCase();
  if ([...normalized].some((char) => !ULID_ENCODING.includes(char))) return null;
  let time = 0;
  for (const char of normalized.slice(0, ULID_TIME_LENGTH)) {
    const value = ULID_ENCODING.indexOf(char);
    if (value < 0) return null;
    time = time * ULID_ENCODING.length + value;
  }
  return Number.isSafeInteger(time) && time <= ULID_MAX_TIME ? time : null;
}

/** Freshness-only Phase-B verdict; blocked records still count as a live rebuild. */
export function hasFreshPhaseBProgressRecord(
  phaseProgress: PhaseProgressUpdate | null | undefined,
  nowMs: number | undefined,
): boolean {
  if (phaseProgress?.kind !== 'derived' || nowMs === undefined) return false;
  const current = phaseProgress.derived.current;
  if (current <= 2 || current >= 7) return false;
  // Reviewer filenames use a per-emission ULID, whose first ten characters
  // durably encode the block event time. Prefer that over the older phase
  // transition so a late block gets its own bounded window and a replayed old
  // block keeps its age across remounts/restarts. Malformed/pre-ULID records
  // fall back to the phase transition and therefore cannot re-arm forever.
  const startedAt = phaseProgress.derived.blockedBy
    ? (reviewerEmissionTimeMs(phaseProgress.derived.blockedBy.emissionId) ??
      phaseProgressStartedAt(phaseProgress))
    : phaseProgressStartedAt(phaseProgress);
  if (startedAt === null) return false;
  const ageMs = nowMs - startedAt;
  return ageMs >= -PHASE_PROGRESS_FUTURE_SKEW_MS && ageMs < STALE_BUILD_NO_LIVE_AGENT_MS;
}

/** Shared run-state/Deploy verdict for recent, unblocked implementation progress. */
export function hasFreshPhaseBProgress(
  phaseProgress: PhaseProgressUpdate | null | undefined,
  nowMs: number | undefined,
): boolean {
  return (
    phaseProgress?.kind === 'derived' &&
    !phaseProgress.derived.blockedBy &&
    hasFreshPhaseBProgressRecord(phaseProgress, nowMs)
  );
}

type WorkspaceRunStateCore = Omit<WorkspaceRunState, 'planRevisionLocked'>;

function terminalDoneRunState(
  interviewStatus: BuildGameInterviewStatus | null,
  debugSource: string,
): WorkspaceRunStateCore {
  const isPartial = interviewStatus === 'built-partial';
  return {
    kind: 'done',
    label: isPartial ? 'Built with warnings' : 'Done',
    detail: isPartial
      ? 'The agent stopped after a partial build.'
      : 'The build-game flow is complete.',
    tone: isPartial ? 'warning' : 'success',
    showBackToPlan: false,
    debugSource,
  };
}

function unverifiedBuildCompletionState(args: {
  showBackToPlan: boolean;
  debugSource: string;
  elapsedSince?: number | null;
}): WorkspaceRunStateCore {
  return {
    kind: 'needs-attention',
    label: 'Build completion could not be verified',
    detail: UNVERIFIED_BUILD_COMPLETION_DETAIL,
    tone: 'warning',
    primaryCta: 'retry',
    showBackToPlan: args.showBackToPlan,
    elapsedSince: args.elapsedSince,
    debugSource: args.debugSource,
  };
}

function staleFinalReviewAttentionState(args: {
  showBackToPlan: boolean;
  elapsedSince: number | null;
  debugSource: 'phase-progress:stale-final-review' | 'kernel:stale-final-review';
}): WorkspaceRunStateCore {
  return {
    kind: 'needs-attention',
    label: 'Build needs attention',
    detail: STALE_FINAL_REVIEW_DETAIL,
    tone: 'warning',
    primaryCta: 'retry',
    showBackToPlan: args.showBackToPlan,
    elapsedSince: args.elapsedSince,
    debugSource: args.debugSource,
  };
}

function staleFinalReviewAttentionFromLegacy(args: {
  signals: WorkspaceRunSignals;
  interview: InterviewSnapshot;
  freshRunningActivity: boolean;
  showBackToPlan: boolean;
}): WorkspaceRunStateCore | null {
  if (args.freshRunningActivity || args.signals.isAiTyping) return null;
  if (isTerminalInterviewStatus(args.interview.status)) return null;
  // #3025 — same false-stale class as the stale-build escalation: a poll-fed
  // build finalizing at review has no activity-snapshot rows, so suppress when
  // the lastActivityAt stamp proves liveness (present + fresh/quiet). Absent or
  // aged-out keeps the legacy escalation. (The kernel final-review path uses the
  // server-authoritative agentHeartbeat, a different signal, and is unchanged.)
  if (activityStampProvesLiveness(args.signals)) return null;
  if (!phaseProgressAtFinalReview(args.signals.phaseProgress)) return null;
  if (phaseProgressBlocked(args.signals.phaseProgress)) return null;

  return staleFinalReviewAttentionState({
    showBackToPlan: args.showBackToPlan,
    elapsedSince: phaseProgressStartedAt(args.signals.phaseProgress),
    debugSource: 'phase-progress:stale-final-review',
  });
}

function phaseProgressInImplementation(progress: PhaseProgressUpdate | null | undefined): boolean {
  return (
    progress?.kind === 'derived' && progress.derived.current > 2 && progress.derived.current < 6
  );
}

/** #3035 — snapshot analogue of `phaseProgressInImplementation`: is the Foundry
 *  snapshot's raw `phase.current` an implementation phase (3-5)? Final review
 *  (6) is owned by `staleFinalReviewAttentionFromKernel`, not this detector. */
function snapshotPhaseInImplementation(current: number | undefined): boolean {
  return typeof current === 'number' && current > 2 && current < 6;
}

/**
 * #3033/#3034 — does the single liveness source PROVE the build is alive, and so
 * SUPPRESS a stale-build / stale-final-review escalation? A poll-fed cloud build
 * has NO activity-snapshot rows, so `freshRunningActivity` is false even while
 * the agent streams; escalating on that alone re-surfaces the false-stale UI one
 * layer up (the escalation renders as "Needs attention" ahead of the liveness
 * branch on both surfaces). Suppress ONLY on POSITIVE proof-of-life
 * (`provenLive`) — a client-OBSERVED advance while fresh/quiet.
 *
 * Round-2 (#3033): a WARM-UP `fresh` (first-seen stamp, no advance observed yet)
 * is NOT proof and must NOT suppress, or a build mounted-into whose pod stamp is
 * already old reads "alive" and masks BOTH the 15-minute wall-clock AND the
 * kernel idle-heartbeat escalations for the whole warm-up window. `provenLive`
 * folds in that distinction plus the tri-state (stalled/unknown ⇒ not proven),
 * so an absent stamp, a genuine stall, and warm-up all leave the #2917 detector
 * armed. Absent signal ⇒ not proven ⇒ don't suppress.
 */
function activityStampProvesLiveness(signals: WorkspaceRunSignals): boolean {
  return signals.provenLive === true;
}

function staleBuildAttentionFromPhaseProgress(args: {
  signals: WorkspaceRunSignals;
  freshRunningActivity: boolean;
  showBackToPlan: boolean;
  nowMs: number | undefined;
}): WorkspaceRunStateCore | null {
  if (args.nowMs === undefined) return null;
  if (args.freshRunningActivity || args.signals.isAiTyping) return null;
  // #3025 — suppress the escalation when the lastActivityAt stamp proves the
  // (rows-less) poll-fed build is still alive. See activityStampProvesLiveness.
  if (activityStampProvesLiveness(args.signals)) return null;
  // 2026-07-10 — removed the redundant `hasRunningActivity(...)` guard that
  // used to sit here (added by d0ea666bb, "soften stale agent build status").
  // That commit's actual intent was cosmetic (WorkspaceRightPanel's "No live
  // agents" → "No recent updates" label for a stale-but-technically-running
  // row) but it ALSO added this check into the escalation path, a different
  // concern. `hasRunningActivity` only checks `status === 'running'` and
  // ignores `.stale` entirely — so once an agent row went stale without ever
  // reaching a terminal SDK chunk (crash, killed pod, hard network break
  // mid-stream), this guard permanently blocked the 15-minute escalation
  // below, no matter how many hours passed. `args.freshRunningActivity`
  // above (via `hasFreshRunningActivity`) already excludes any genuinely
  // fresh (non-stale) running agent, so removing this line restores the
  // intended contract: a build stuck at the same phase for
  // STALE_BUILD_NO_LIVE_AGENT_MS with no FRESH activity now correctly
  // escalates — matching real production data (a build's
  // docs/build-progress.json carried one phase-4 transition, then nothing,
  // while an activity row sat at status:'running', stale:true for 20+ minutes).
  if (!phaseProgressInImplementation(args.signals.phaseProgress)) return null;
  if (phaseProgressBlocked(args.signals.phaseProgress)) return null;

  const elapsedSince = phaseProgressStartedAt(args.signals.phaseProgress);
  if (elapsedSince === null) return null;
  if (args.nowMs - elapsedSince < STALE_BUILD_NO_LIVE_AGENT_MS) return null;

  return {
    kind: 'needs-attention',
    label: 'Build needs attention',
    detail: STALE_BUILD_DETAIL,
    tone: 'warning',
    primaryCta: 'retry',
    showBackToPlan: args.showBackToPlan,
    elapsedSince,
    debugSource: 'phase-progress:stale-build',
  };
}

function kernelHeartbeatHasFreshWork(
  heartbeat: FoundryGameStateSnapshot['agentHeartbeat'],
): boolean {
  return heartbeat.status === 'running' && heartbeat.activeAgentIds.length > 0;
}

function kernelHeartbeatHasNoFreshWork(
  heartbeat: FoundryGameStateSnapshot['agentHeartbeat'],
): boolean {
  return (
    heartbeat.activeAgentIds.length === 0 &&
    (heartbeat.status === 'idle' || heartbeat.status === 'stale' || heartbeat.status === 'closed')
  );
}

function staleFinalReviewAttentionFromKernel(args: {
  snapshot: FoundryGameStateSnapshot;
  interviewStatus: BuildGameInterviewStatus | null;
  isAiTyping: boolean;
  elapsedSince: number | null;
}): WorkspaceRunStateCore | null {
  if (args.isAiTyping) return null;
  if (isTerminalInterviewStatus(args.interviewStatus)) return null;
  if (args.snapshot.build.terminal || args.snapshot.build.status !== 'running') return null;
  if (args.snapshot.phase.current !== 6) return null;
  if (args.snapshot.phase.blockedBy) return null;
  if (!kernelHeartbeatHasNoFreshWork(args.snapshot.agentHeartbeat)) return null;

  return staleFinalReviewAttentionState({
    showBackToPlan: true,
    elapsedSince: args.elapsedSince,
    debugSource: 'kernel:stale-final-review',
  });
}

/**
 * #3035 — snapshot-aware implementation-phase (3-5) stale-build detector for the
 * DEGRADED path: the phase-progress subscription is undefined (it starts
 * undefined and swallows subscribe failures) but the Foundry snapshot
 * independently reports the phase. It is the implementation-phase sibling of
 * `staleFinalReviewAttentionFromKernel` (phase 6), and closes the gap that left
 * a build gone genuinely quiet mid-implementation on this path rendering
 * 'Building' forever — `staleBuildAttentionFromPhaseProgress` is 100%
 * phase-progress-driven and returns null when `phaseProgress` is undefined.
 *
 * Same ASYMMETRIC liveness guard as every sibling escalation: POSITIVE
 * proof-of-life (`provenLive` — a client-observed advance while fresh/quiet)
 * SUPPRESSES; aged / warm-up (no proven advance yet) stays ARMED and escalates
 * only after the stale window, so it can neither false-alarm a live build nor
 * suppress a genuinely-stuck one.
 *
 * KNOWN-epoch gate (#3035 round-3): escalate ONLY when the snapshot carries a
 * durable `build.buildRunId`. An unknown epoch (a legacy / version-skewed pod
 * that omits `buildRunId`) is not a stable run identity, so the client-relative
 * phase-start anchor can't be trusted across a possible retry into the same phase
 * — fail SAFE and do not escalate (the accepted legacy-null residual; #2917 for
 * such pods is covered by the kernel final-review + phase-progress backstops).
 * This is also what lets the phase-start memo hold a STABLE anchor for all epochs
 * (no per-render re-anchor churn feeding the `workspaceRunState` memo).
 *
 * The window anchor is CLIENT-RELATIVE (`signals.snapshotPhaseStartedAt`, stamped
 * by `useActivityLiveness` when the snapshot phase was first observed on the
 * client clock): the snapshot exposes no phase-start timestamp, and the
 * client-relative anchor needs NO runtime change, NO new snapshot field, and NO
 * version bump (the #3098 pattern this reuses). When the phase-progress
 * subscription IS live (derived), that path is authoritative and owns the
 * escalation, so this detector defers to it.
 */
function staleBuildAttentionFromKernel(args: {
  signals: WorkspaceRunSignals;
  freshRunningActivity: boolean;
  showBackToPlan: boolean;
  nowMs: number | undefined;
}): WorkspaceRunStateCore | null {
  const { signals } = args;
  if (args.nowMs === undefined) return null;
  // Snapshot-only degraded path only: defer to
  // `staleBuildAttentionFromPhaseProgress` whenever the subscription is live.
  if (signals.phaseProgress?.kind === 'derived') return null;
  const snapshot = signals.foundrySnapshot;
  if (!snapshot) return null;
  if (snapshot.build.terminal || snapshot.build.status !== 'running') return null;
  // Known-epoch gate: no durable build run id ⇒ unknown epoch ⇒ fail safe (see
  // the doc block). Keeps the phase-start memo churn-free and the anchor trusted.
  if (!snapshot.build.buildRunId) return null;
  if (!snapshotPhaseInImplementation(snapshot.phase.current)) return null;
  if (snapshot.phase.blockedBy) return null;
  if (args.freshRunningActivity || signals.isAiTyping) return null;
  // Asymmetric guard: only POSITIVE proof-of-life suppresses (see
  // activityStampProvesLiveness). Absent/aged/warm-up ⇒ stays armed.
  if (activityStampProvesLiveness(signals)) return null;

  // Client-relative phase-start anchor (no runtime phase-start timestamp rides
  // the snapshot). Absent ⇒ cannot age a window ⇒ do not escalate.
  const elapsedSince = signals.snapshotPhaseStartedAt;
  if (typeof elapsedSince !== 'number') return null;
  if (args.nowMs - elapsedSince < STALE_BUILD_NO_LIVE_AGENT_MS) return null;

  return {
    kind: 'needs-attention',
    label: 'Build needs attention',
    detail: STALE_BUILD_DETAIL,
    tone: 'warning',
    primaryCta: 'retry',
    showBackToPlan: args.showBackToPlan,
    elapsedSince,
    debugSource: 'kernel:stale-build',
  };
}

function kickoffAttention(
  kickoff: InitialKickoffState,
  buildInFlight: boolean,
): WorkspaceRunStateCore | null {
  if (kickoff.status !== 'timed-out' && kickoff.status !== 'failed') {
    return null;
  }
  const reason = kickoff.reason ?? 'agent-send-rejected';
  // crucible#2345 — the renderer-ready handshake can time out on a perfectly
  // HEALTHY cloud-VM build: the auto-build orchestrator drives the turn
  // server-side with zero renderer questions, so the per-slot
  // `<InterviewSlotOverlay>` never fires `interview.ready@1` and the 15s kickoff
  // watchdog times out while chunks are streaming. Do NOT scare a non-technical
  // user with "Interview did not start" when the build is actually in-flight —
  // let the computation fall through to the live build / auto-build state. Only
  // a GENUINE renderer stall (no server-side activity) warrants attention. This
  // is SCOPED to `renderer-not-ready-timeout`: every other failure reason
  // (token/proxy/state/dispatch) is a real fault and still surfaces regardless.
  if (reason === 'renderer-not-ready-timeout' && buildInFlight) {
    return null;
  }
  const detailByReason: Record<InitialKickoffFailureReason, string> = {
    'renderer-not-ready-timeout': 'The interview UI did not become ready in time.',
    'agent-send-rejected': 'The first agent message could not be sent.',
    'interview-state-missing': 'The interview state file is missing.',
    'anthropic-token-missing': 'Add an Anthropic key in Settings to start the interview.',
    'cloud-proxy-missing': 'Cloud-mode proxy configuration is missing.',
    'turn-in-flight': 'A previous turn is still winding down.',
  };
  return {
    kind: 'needs-attention',
    label: 'Interview did not start',
    detail: kickoff.detail ?? detailByReason[reason],
    tone: 'error',
    primaryCta: 'retry',
    showBackToPlan: false,
    elapsedSince: kickoff.startedAt ?? null,
    debugSource: `kickoff:${kickoff.status}:${reason}`,
  };
}

function kickoffActive(kickoff: InitialKickoffState): WorkspaceRunStateCore | null {
  if (kickoff.status !== 'waiting-for-renderer-ready' && kickoff.status !== 'dispatching') {
    return null;
  }
  const detailByStatus: Record<InitialKickoffStatus, string> = {
    idle: '',
    'waiting-for-renderer-ready': 'Preparing interview controls.',
    dispatching: 'Sending the first build-game turn.',
    dispatched: '',
    'timed-out': '',
    failed: '',
  };
  return {
    kind: 'starting-interview',
    label: 'Starting interview',
    detail: detailByStatus[kickoff.status],
    tone: 'active',
    showBackToPlan: false,
    disableComposerReason: 'Interview startup is in progress.',
    elapsedSince: kickoff.startedAt ?? null,
    debugSource: `kickoff:${kickoff.status}`,
  };
}

function quickBackToPlanEligible(
  depth: KnownInterviewDepth | undefined,
  subPhase: KnownInterviewSubPhase | undefined,
): boolean {
  return depth === 'quick' && (subPhase === 'committed' || subPhase === 'build');
}

function currentPlanWasHandedToBuild(interview: InterviewSnapshot): boolean {
  return savedPlanMatchesBuilt({
    planRevision: interview.planRevision,
    planHash: interview.planHash,
    lastBuiltPlanRevision: interview.lastBuiltPlanRevision,
    lastBuiltPlanHash: interview.lastBuiltPlanHash,
  });
}

function hasBuildDispatchEvidence(interview: InterviewSnapshot): boolean {
  return (
    !interview.exists ||
    interview.status === 'dispatched' ||
    interview.status === 'building' ||
    interview.status === 'built' ||
    interview.status === 'built-partial' ||
    interview.subPhase === 'committed' ||
    interview.subPhase === 'build' ||
    Boolean(interview.dispatchedAt) ||
    interview.buildDispatchState === 'accepted' ||
    interview.autoBuild !== null ||
    currentPlanWasHandedToBuild(interview)
  );
}

function snapshotRequiresBuildDispatch(state: WorkspaceRunState): boolean {
  return (
    // Kernel pending actions are emitted by the build agent. A current Guided
    // interview without accepted dispatch authority must ignore leftovers from
    // an older build-events snapshot and derive its own interview state.
    state.debugSource.startsWith('kernel:pending-action:') ||
    state.debugSource.startsWith('kernel:phase:') ||
    state.debugSource.startsWith('kernel:build:') ||
    state.debugSource.startsWith('kernel:terminal:') ||
    state.debugSource === 'kernel:stale-build' ||
    state.debugSource === 'kernel:stale-final-review'
  );
}

function buildHandoffPendingRunState(args: {
  signals: WorkspaceRunSignals;
  interview: InterviewSnapshot;
  freshRunningActivity: boolean;
}): WorkspaceRunStateCore | null {
  if (!currentPlanWasHandedToBuild(args.interview)) return null;
  if (isTerminalInterviewStatus(args.interview.status)) return null;
  const showBackToPlan = args.interview.depth === 'quick';

  const phaseBActivityStarted =
    args.freshRunningActivity || phaseProgressBeyondPlanReview(args.signals.phaseProgress);
  if (phaseBActivityStarted) {
    return {
      kind: 'building',
      label: 'Building',
      detail: 'Phase B has started.',
      tone: 'active',
      primaryCta: showBackToPlan ? 'back-to-plan' : undefined,
      showBackToPlan,
      elapsedSince: runningStartedAt(args.signals.activitySnapshot),
      debugSource: 'plan-handoff:build-started',
    };
  }

  const watchdog = args.signals.buildKickoffWatchdog;
  if (watchdog?.status === 'stalled' || watchdog?.status === 'resume-attempted') {
    return {
      kind: 'needs-attention',
      label: 'Start building stalled',
      detail: 'The plan was sent to build, but Phase B did not start.',
      tone: 'warning',
      primaryCta: 'retry',
      showBackToPlan,
      elapsedSince: watchdog.stalledAt ?? watchdog.committedAt ?? null,
      debugSource: `plan-handoff:watchdog-${watchdog.status}`,
    };
  }

  return {
    kind: 'build-kickoff-pending',
    label: 'Build kickoff pending',
    detail: 'Waiting for Phase B to start.',
    tone: 'active',
    primaryCta: showBackToPlan ? 'back-to-plan' : undefined,
    showBackToPlan,
    elapsedSince: watchdog?.committedAt ?? runningStartedAt(args.signals.activitySnapshot),
    debugSource:
      watchdog?.status === 'pending'
        ? 'plan-handoff:watchdog-pending'
        : 'plan-handoff:current-plan',
  };
}

function blockedBuildAttention(
  signals: WorkspaceRunSignals,
  freshRunningActivity: boolean,
  showBackToPlan: boolean,
): WorkspaceRunStateCore | null {
  const blockedPhase =
    signals.phaseProgress?.kind === 'derived' ? signals.phaseProgress.derived.blockedBy : undefined;
  if (!blockedPhase || freshRunningActivity) {
    return null;
  }
  return {
    kind: 'needs-attention',
    label: 'Build blocked',
    detail: `Blocked by ${blockedPhase.reviewer} review.`,
    tone: 'warning',
    showBackToPlan,
    debugSource: 'phase-progress:blocked',
  };
}

function phaseProgressRunState(
  signals: WorkspaceRunSignals,
  showBackToPlan: boolean,
): WorkspaceRunStateCore | null {
  if (signals.phaseProgress?.kind !== 'derived') return null;
  const current = signals.phaseProgress.derived.current;
  const elapsedSince =
    runningStartedAt(signals.activitySnapshot) ?? phaseProgressStartedAt(signals.phaseProgress);

  if (current === 1) {
    return {
      kind: 'drafting-plan',
      label: 'Drafting plan',
      detail: 'The agent is turning your answers into a build plan.',
      tone: 'active',
      showBackToPlan: false,
      elapsedSince,
      debugSource: 'phase-progress:1',
    };
  }

  if (current === 2) {
    return {
      kind: 'waiting-for-plan-review',
      label: 'Review Build Plan',
      detail: 'Review the plan, refine it, or start building.',
      tone: 'waiting',
      primaryCta: 'start-building',
      showBackToPlan: false,
      elapsedSince,
      debugSource: 'phase-progress:2',
    };
  }

  if (current >= 7) {
    const transitionAt = [...signals.phaseProgress.derived.transitions]
      .reverse()
      .find((transition) => transition.phase === current)?.at;
    if (
      !hasVerifiedBuildCompletionEvidence({
        progress: {
          currentPhase: current,
          lastTransitionAt: transitionAt,
        },
        // Mirror the state kernel cutoff so stale phase progress cannot verify a newer plan.
        freshnessCutoffAt: legacyPhaseProgressFreshnessCutoffAt(signals.interviewState),
      })
    ) {
      return unverifiedBuildCompletionState({
        showBackToPlan,
        elapsedSince,
        debugSource: 'phase-progress:7:completion-unverified',
      });
    }
    return {
      kind: 'done',
      label: 'Ready for user testing',
      detail: 'The phase tracker is at Phase 7.',
      tone: 'success',
      showBackToPlan: false,
      debugSource: 'phase-progress:7',
    };
  }

  if (current > 2) {
    return {
      kind: 'building',
      label: 'Building',
      detail: signals.phaseProgress.derived.label || 'The agent is working through implementation.',
      tone: 'active',
      primaryCta: showBackToPlan ? 'back-to-plan' : undefined,
      showBackToPlan,
      elapsedSince,
      debugSource: 'phase-progress:build',
    };
  }

  return null;
}

function autoBuildRunState(autoBuild: AutoBuildState | null): WorkspaceRunStateCore | null {
  if (!autoBuild || autoBuild.status === 'done') return null;
  const elapsedSince = parseIsoTimeMs(autoBuild.updatedAt) ?? parseIsoTimeMs(autoBuild.armedAt);

  switch (autoBuild.status) {
    case 'armed':
      return {
        kind: 'drafting-plan',
        label: 'Preparing build',
        detail: 'Queued the automatic design-doc and build-plan pipeline.',
        tone: 'active',
        showBackToPlan: false,
        elapsedSince,
        debugSource: `auto-build:${autoBuild.mode}:armed`,
      };
    case 'generating-docs':
      return {
        kind: 'drafting-plan',
        label: 'Preparing build',
        detail: 'Generating design docs from the prompt and interview answers.',
        tone: 'active',
        showBackToPlan: false,
        elapsedSince,
        debugSource: `auto-build:${autoBuild.mode}:generating-docs`,
      };
    case 'exporting-package':
      return {
        kind: 'drafting-plan',
        label: 'Preparing build',
        detail: 'Creating the build plan and handoff package.',
        tone: 'active',
        showBackToPlan: false,
        elapsedSince,
        debugSource: `auto-build:${autoBuild.mode}:exporting-package`,
      };
    case 'commit-starting':
      return {
        kind: 'build-kickoff-pending',
        label: 'Starting build',
        detail: 'Committing the design snapshot and dispatching the build.',
        tone: 'active',
        showBackToPlan: false,
        elapsedSince,
        debugSource: `auto-build:${autoBuild.mode}:commit-starting`,
      };
    case 'cancelled':
      return {
        kind: 'needs-attention',
        label: 'Build setup cancelled',
        detail: 'Build setup stopped before the snapshot was committed.',
        tone: 'warning',
        primaryCta: 'retry',
        showBackToPlan: false,
        elapsedSince,
        debugSource: `auto-build:${autoBuild.mode}:cancelled`,
      };
    case 'failed':
      return {
        kind: 'needs-attention',
        label: 'Build setup failed',
        detail: autoBuild.error ?? 'The automatic build setup failed before the snapshot commit.',
        tone: 'error',
        primaryCta: 'retry',
        showBackToPlan: false,
        elapsedSince,
        debugSource: `auto-build:${autoBuild.mode}:failed`,
      };
  }
}

function formatChoiceList(labels: string[]): string {
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0]!;
  if (labels.length === 2) return `${labels[0]} or ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, or ${labels[labels.length - 1]}`;
}

function phaseBDecisionDetail(
  decision: NonNullable<InterviewSnapshot['pendingPhaseBDecision']>,
): string {
  const labels =
    decision.optionIds
      ?.map((id) => decision.optionLabels?.[id] ?? id)
      .filter((label) => label.trim().length > 0) ?? [];
  const choices = formatChoiceList(labels);
  if (choices) {
    return `Choose: ${choices}.`;
  }

  const category = decision.category;
  switch (category) {
    case 'reviewer-block':
      return 'Choose how to handle the reviewer block.';
    case 'reviewer-concerns-high':
      return 'Choose how to handle the reviewer concerns.';
    case 'install-failed':
      return 'Choose how to handle the failed install.';
    case 'missing-env-var':
      return 'Choose how to handle the missing environment variable.';
    case 'destructive-confirm':
      return 'Confirm how to proceed.';
    default:
      return 'Choose a path forward for the build.';
  }
}

function parseIsoTimeMs(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Return the state-kernel freshness marker for identity-less legacy phase progress.
 *
 * Args:
 *   state: Persisted build-game interview state from the legacy progress path.
 *
 * Returns:
 *   The newest relevant ISO timestamp, or undefined when none are parseable.
 *
 * Raises:
 *   None.
 */
function legacyPhaseProgressFreshnessCutoffAt(
  state: BuildGameInterviewState | null | undefined,
): string | undefined {
  let latest: { value: string; time: number } | null = null;
  for (const value of [state?.startedAt, state?.dispatchedAt ?? undefined, state?.planUpdatedAt]) {
    if (!value) continue;
    const time = parseIsoTimeMs(value);
    if (time === null) continue;
    if (!latest || time > latest.time) latest = { value, time };
  }
  return latest?.value;
}

function withPlanRevisionLocked(
  state: Omit<WorkspaceRunState, 'planRevisionLocked'>,
  interviewStatus: BuildGameInterviewStatus | null,
): WorkspaceRunState {
  return {
    ...state,
    planRevisionLocked: planRevisionLockedForHandoff({
      runStateKind: state.kind,
      showBackToPlan: state.showBackToPlan,
      interviewStatus,
    }),
  };
}

function withIntakeAuthority(
  state: WorkspaceRunState,
  signals: WorkspaceRunSignals,
): WorkspaceRunState {
  const intakeOwnedProjection =
    state.debugSource.startsWith('kickoff:') ||
    state.debugSource.startsWith('interview-state:') ||
    state.debugSource === 'interview-status:in-progress';
  // Phase progress is durable and can outlive its owner. Only a client-observed,
  // current-epoch activity advance proves that a phase-one snapshot is still live.
  const phaseOnePlanWorkLive =
    signals.phaseProgress?.kind === 'derived' &&
    signals.phaseProgress.derived.current === 1 &&
    !phaseProgressBlocked(signals.phaseProgress) &&
    signals.provenLive === true;
  const interviewOrPlanWorkLive =
    signals.isAiTyping || hasFreshRunningActivity(signals.activitySnapshot) || phaseOnePlanWorkLive;
  if (
    signals.intakeAuthorityMissing !== true ||
    interviewOrPlanWorkLive ||
    !intakeOwnedProjection ||
    (state.kind !== 'starting-interview' &&
      state.kind !== 'interview-in-progress' &&
      state.kind !== 'drafting-plan')
  ) {
    return state;
  }

  return {
    kind: 'needs-attention',
    label: 'Interview needs attention',
    detail:
      'The saved build state expects an interview, but Foundry found no active or recoverable interview.',
    tone: 'warning',
    primaryCta: 'retry',
    showBackToPlan: false,
    planRevisionLocked: state.planRevisionLocked,
    debugSource: 'intake-authority:missing',
  };
}

export function deriveWorkspaceRunState(signals: WorkspaceRunSignals): WorkspaceRunState {
  const interview = interviewSnapshot(signals.interviewState, signals.interviewStatus);
  // Durable review identity is the approval boundary. It outranks process-local
  // kickoff/chat failures and stale liveness left by the completed interview
  // turn, but only while no matching approval/build evidence exists.
  if (isGuidedPlanAwaitingApproval(signals.interviewState)) {
    return withPlanRevisionLocked(
      {
        kind: 'waiting-for-plan-review',
        label: 'Review Build Plan',
        detail: 'Review the plan, refine it, or start building.',
        tone: 'waiting',
        primaryCta: 'start-building',
        showBackToPlan: false,
        elapsedSince: signals.interviewState?.guidedPlanReview
          ? parseIsoTimeMs(signals.interviewState.guidedPlanReview.readyAt)
          : null,
        debugSource: 'interview-state:guided-plan-review:awaiting-approval',
      },
      interview.status,
    );
  }
  const buildDispatched =
    hasBuildDispatchEvidence(interview) ||
    signals.foundrySnapshot?.build.source === 'build-game-interview.json';
  const operationalError = signals.planModeError ?? signals.chatPersistError?.message ?? null;
  if (!operationalError) {
    const snapshotState = deriveWorkspaceRunStateFromFoundrySnapshot(signals.foundrySnapshot, {
      interviewStatus: interview.status,
      isAiTyping: signals.isAiTyping,
      // #3025 round-4: suppress the snapshot-first kernel final-review escalation
      // when the poll stamp proves the build is alive (fresh|quiet). Unknown/aged
      // ⇒ false ⇒ the detector stays armed.
      stampProvesLiveness: activityStampProvesLiveness(signals),
    });
    const activeLegacySummarySupersedesCompletedSnapshot =
      interview.legacyCompletionUnverified &&
      (interview.status === 'dispatched' || interview.status === 'building') &&
      (snapshotState?.kind === 'done' || isCompletionVerificationRetryRunState(snapshotState));
    const freshRunningActivity =
      hasFreshRunningActivity(signals.activitySnapshot) ||
      (signals.foundrySnapshot
        ? kernelHeartbeatHasFreshWork(signals.foundrySnapshot.agentHeartbeat)
        : false);
    if (snapshotState?.kind === 'building' && buildDispatched) {
      // Phase-progress path first (authoritative when the subscription is live);
      // fall back to the #3035 snapshot-only implementation-phase detector when
      // it is undefined but the snapshot reports a 3-5 phase.
      const staleBuild =
        staleBuildAttentionFromPhaseProgress({
          signals,
          freshRunningActivity,
          showBackToPlan: snapshotState.showBackToPlan,
          nowMs: signals.nowMs,
        }) ??
        staleBuildAttentionFromKernel({
          signals,
          freshRunningActivity,
          showBackToPlan: snapshotState.showBackToPlan,
          nowMs: signals.nowMs,
        });
      if (staleBuild) {
        return withPlanRevisionLocked(staleBuild, interview.status);
      }
    }
    if (
      snapshotState &&
      !activeLegacySummarySupersedesCompletedSnapshot &&
      (buildDispatched || !snapshotRequiresBuildDispatch(snapshotState)) &&
      !(
        snapshotState.kind === 'waiting-for-plan-review' &&
        ((snapshotIsImplicitPlanReview(signals.foundrySnapshot) &&
          hasFreshBuildAgentActivity(signals.activitySnapshot)) ||
          currentPlanWasHandedToBuild(interview))
      )
    ) {
      return withIntakeAuthority(snapshotState, signals);
    }
  }
  return withIntakeAuthority(
    withPlanRevisionLocked(deriveWorkspaceRunStateCore(signals, interview), interview.status),
    signals,
  );
}

export function deriveWorkspaceRunStateFromFoundrySnapshot(
  snapshot: FoundryGameStateSnapshot | null | undefined,
  options: {
    interviewStatus?: BuildGameInterviewStatus | null;
    isAiTyping?: boolean;
    /** #3025 round-4 — a fresh|quiet lastActivityAt suppresses the kernel
     *  final-review escalation (heartbeat/reality mismatch guard). Defaults
     *  false = legacy behaviour for callers without poll liveness. */
    stampProvesLiveness?: boolean;
  } = {},
): WorkspaceRunState | null {
  const interviewStatus = options.interviewStatus ?? null;
  const core = deriveWorkspaceRunStateCoreFromFoundrySnapshot(
    snapshot,
    interviewStatus,
    options.isAiTyping ?? false,
    options.stampProvesLiveness ?? false,
  );
  if (!core) return null;
  return withPlanRevisionLocked(core, interviewStatus);
}

function deriveWorkspaceRunStateCoreFromFoundrySnapshot(
  snapshot: FoundryGameStateSnapshot | null | undefined,
  interviewStatus: BuildGameInterviewStatus | null = null,
  isAiTyping = false,
  stampProvesLiveness = false,
): WorkspaceRunStateCore | null {
  if (!snapshot) return null;

  if (
    snapshot.layout.health.status === 'maintenance-required' ||
    snapshot.build.status === 'maintenance-required'
  ) {
    const detail =
      snapshot.build.reason ??
      snapshot.layout.health.reason ??
      snapshot.layout.repair.reason ??
      snapshot.layout.repair.actions[0] ??
      'Foundry cannot safely interpret this game state.';
    return {
      kind: 'needs-attention',
      label: 'State maintenance required',
      detail,
      tone: 'warning',
      showBackToPlan: false,
      disableComposerReason: 'Run state maintenance before continuing.',
      debugSource: 'kernel:maintenance-required',
    };
  }

  if (snapshot.pendingAction.kind === 'phase-b-decision') {
    return {
      kind: 'waiting-on-user',
      label: 'Waiting on you',
      detail: 'Choose a path forward for the build.',
      tone: 'waiting',
      primaryCta: 'back-to-plan',
      showBackToPlan: true,
      debugSource: `kernel:pending-action:${snapshot.pendingAction.id ?? 'phase-b-decision'}`,
    };
  }

  if (snapshot.pendingAction.kind === 'plan-review') {
    return {
      kind: 'waiting-for-plan-review',
      label: 'Review Build Plan',
      detail: snapshot.pendingAction.reason ?? 'Review the plan, refine it, or start building.',
      tone: 'waiting',
      primaryCta: 'start-building',
      showBackToPlan: false,
      debugSource: `kernel:pending-action:${snapshot.pendingAction.id ?? 'plan-review'}`,
    };
  }

  if (isGeneralUserWaitKind(snapshot.pendingAction.kind)) {
    return {
      kind: 'waiting-on-user',
      label: 'Waiting on you',
      detail: snapshot.pendingAction.reason ?? 'Foundry is waiting for input.',
      tone: 'waiting',
      showBackToPlan: false,
      debugSource: `kernel:pending-action:${snapshot.pendingAction.id ?? snapshot.pendingAction.kind}`,
    };
  }

  if (snapshot.build.terminal) {
    if (snapshot.build.status === 'failed') {
      return {
        kind: 'needs-attention',
        label: 'Build failed',
        detail: snapshot.build.reason ?? 'The build-game flow failed.',
        tone: 'error',
        primaryCta: 'retry',
        showBackToPlan: false,
        debugSource: `kernel:terminal:${snapshot.build.source ?? 'unknown'}:failed`,
      };
    }
    if (
      snapshot.build.status === 'completed' &&
      !hasVerifiedBuildCompletionEvidence({
        currentBuildRunId: snapshot.build.buildRunId,
        terminal: {
          terminalStatus: 'completed',
          buildRunId: snapshot.build.buildRunId,
        },
      })
    ) {
      return unverifiedBuildCompletionState({
        showBackToPlan: false,
        debugSource: `kernel:terminal:${snapshot.build.source ?? 'unknown'}:unverified`,
      });
    }
    const terminalSuffix = interviewStatus === 'built-partial' ? 'built-partial' : 'completed';
    return terminalDoneRunState(
      interviewStatus,
      `kernel:terminal:${snapshot.build.source ?? 'unknown'}:${terminalSuffix}`,
    );
  }

  if (snapshot.build.status !== 'running') {
    return null;
  }

  const elapsedSince = parseIsoTimeMs(snapshot.agentHeartbeat.lastHeartbeatAt);
  const current = snapshot.phase.current;
  if (
    current !== undefined &&
    current >= 7 &&
    (snapshot.build.reason === 'phase-7-unverified' || isTerminalInterviewStatus(interviewStatus))
  ) {
    // Phase 7 with an unverified projection is retryable completion, not active work.
    return unverifiedBuildCompletionState({
      showBackToPlan: true,
      elapsedSince,
      debugSource: `kernel:phase:${current}:completion-unverified`,
    });
  }
  // #3025 round-4: this kernel final-review escalation fires purely from an idle
  // agentHeartbeat, but a poll-fed build can show an idle heartbeat while
  // lastActivityAt is fresh — the heartbeat/reality mismatch this campaign kills
  // (#2917). It runs BEFORE the legacy final-review branch (snapshot-first),
  // which is how it slipped rounds 1-3. When we hold fresh first-hand evidence
  // of life (`stampProvesLiveness`), suppress the escalation; an absent/aged
  // stamp keeps the OLD behaviour so the detector stays armed on legacy pods and
  // genuinely-dead builds (same #2917-preservation rule as rounds 2-3).
  const staleFinalReview = stampProvesLiveness
    ? null
    : staleFinalReviewAttentionFromKernel({
        snapshot,
        interviewStatus,
        isAiTyping,
        elapsedSince,
      });
  if (staleFinalReview) return staleFinalReview;

  if (current === 1) {
    return {
      kind: 'drafting-plan',
      label: 'Drafting plan',
      detail: 'The agent is turning your answers into a build plan.',
      tone: 'active',
      showBackToPlan: false,
      elapsedSince,
      debugSource: 'kernel:phase:1',
    };
  }

  if (current === 2) {
    return {
      kind: 'waiting-for-plan-review',
      label: 'Review Build Plan',
      detail: 'Review the plan, refine it, or start building.',
      tone: 'waiting',
      primaryCta: 'start-building',
      showBackToPlan: false,
      elapsedSince,
      debugSource: 'kernel:phase:2',
    };
  }

  if (current && current > 2) {
    return {
      kind: 'building',
      label: 'Building',
      detail: snapshot.phase.label ?? 'The agent is working through implementation.',
      tone: 'active',
      primaryCta: 'back-to-plan',
      showBackToPlan: true,
      elapsedSince,
      debugSource: `kernel:phase:${current}`,
    };
  }

  return {
    kind: 'building',
    label: 'Building',
    detail: 'The agent is working through implementation.',
    tone: 'active',
    showBackToPlan: false,
    elapsedSince,
    debugSource: 'kernel:build:running',
  };
}

function isGeneralUserWaitKind(kind: FoundryGameStateSnapshot['pendingAction']['kind']): boolean {
  return (
    kind === 'ask-user-question' ||
    kind === 'bulk-interview' ||
    kind === 'dependency-approval' ||
    kind === 'live-preview' ||
    kind === 'scaffold-first' ||
    kind === 'change-request' ||
    kind === 'template-file-override'
  );
}

function deriveWorkspaceRunStateCore(
  signals: WorkspaceRunSignals,
  interview: InterviewSnapshot,
): WorkspaceRunStateCore {
  const showBackToPlan = quickBackToPlanEligible(interview.depth, interview.subPhase);
  const autoBuild = signals.autoBuildState ?? interview.autoBuild;

  const operationalError = signals.planModeError ?? signals.chatPersistError?.message ?? null;
  if (operationalError) {
    return {
      kind: 'needs-attention',
      label: 'Needs attention',
      detail: operationalError,
      tone: 'error',
      primaryCta: 'retry',
      showBackToPlan,
      debugSource: 'error:workspace',
    };
  }

  // crucible#2345 — computed BEFORE the kickoff block so the kickoff watchdog can
  // tell whether the build is actually in-flight (an active auto-build is one of
  // the live-build signals). Returned below, after the kickoff check, so the
  // existing precedence (kickoff attention → auto-build → activity) is preserved.
  const autoBuildState = autoBuildRunState(autoBuild);

  const kickoff = signals.initialKickoff ?? null;
  const kickoffInProgress = kickoff !== null && kickoff.status !== 'idle';
  if (kickoffInProgress) {
    // Is the build genuinely streaming/progressing server-side? If so, a
    // renderer-ready handshake timeout is benign (cloud-VM orchestrator drives
    // the turn with zero renderer questions), and kickoffAttention
    // suppresses the scary "Interview did not start" for that reason only.
    const buildInFlight =
      signals.isAiTyping === true ||
      isTerminalInterviewStatus(signals.interviewStatus) ||
      autoBuildState !== null ||
      hasFreshRunningActivity(signals.activitySnapshot);
    const attention = kickoffAttention(kickoff, buildInFlight);
    if (attention) return attention;
  }

  if (autoBuildState) return autoBuildState;

  const wait = signals.activitySnapshot
    ? pickOldestWait(signals.activitySnapshot.outstandingWaits)
    : null;
  const heartbeatWaiting = signals.activitySnapshot?.heartbeatState === 'waiting';
  const freshRunningActivity = hasFreshRunningActivity(signals.activitySnapshot);
  const staleInterviewAt =
    interview.status === 'in-progress' &&
    !signals.isAiTyping &&
    !freshRunningActivity &&
    signals.provenLive !== true
      ? staleInterviewProgressAt(signals.interviewProgressAt, signals.interviewNowMs)
      : null;
  if (wait && (heartbeatWaiting || !freshRunningActivity)) {
    return {
      kind: 'waiting-on-user',
      label: 'Waiting on you',
      detail: formatWaitLabel(wait),
      tone: 'waiting',
      primaryCta:
        interview.depth === 'quick' &&
        (interview.subPhase === 'plan' ||
          interview.subPhase === 'refine' ||
          interview.subPhase === 'back-to-plan')
          ? 'start-building'
          : undefined,
      showBackToPlan,
      elapsedSince: wait.openedAt,
      debugSource: `activity:wait:${wait.kind}`,
    };
  }

  if (
    signals.isInterviewWaitingForUser ||
    (interview.status === 'in-progress' &&
      interview.depth !== 'quick' &&
      staleInterviewAt === null &&
      !freshRunningActivity &&
      !signals.isAiTyping)
  ) {
    return {
      kind: 'waiting-on-user',
      label: 'Waiting on you',
      detail: 'Answer the interview questions.',
      tone: 'waiting',
      showBackToPlan,
      elapsedSince: runningStartedAt(signals.activitySnapshot),
      debugSource: signals.isInterviewWaitingForUser
        ? 'interview-ui:waiting-for-user'
        : 'interview-status:in-progress:no-running-activity',
    };
  }

  if (kickoffInProgress) {
    const active = kickoffActive(kickoff);
    if (active) return active;
  }

  // Response-only compatibility evidence from the main-process reconciler says
  // legacy build output exists while deliberately granting no modern terminal
  // authority. Canonical kernel state wins in deriveWorkspaceRunState, and the
  // independently-updating kickoff/auto-build/wait signals win above. Preserve
  // live renderer/activity evidence here too; only the otherwise-resting legacy
  // state becomes Ready instead of laundering the summary's `in-progress` into
  // a stale-interview Retry action.
  if (interview.legacyCompletionUnverified) {
    if (interview.status === 'dispatched') {
      return {
        kind: 'build-kickoff-pending',
        label: 'Build kickoff pending',
        detail: 'Interview complete; waiting for implementation to start.',
        tone: 'active',
        showBackToPlan,
        debugSource: 'interview-status:dispatched',
      };
    }
    if (interview.status === 'building') {
      return {
        kind: 'building',
        label: 'Building',
        detail: 'The agent is working through implementation.',
        tone: 'active',
        showBackToPlan,
        elapsedSince: runningStartedAt(signals.activitySnapshot),
        debugSource: 'interview-status:building',
      };
    }
    if (signals.isAiTyping) {
      return {
        kind: 'interview-in-progress',
        label: 'Interview in progress',
        detail: 'The agent is asking questions or processing your answer.',
        tone: 'active',
        showBackToPlan,
        elapsedSince: runningStartedAt(signals.activitySnapshot),
        debugSource: 'chat:is-ai-typing',
      };
    }
    if (freshRunningActivity) {
      return {
        kind: 'building',
        label: 'Agent working',
        detail: 'Live activity is in progress.',
        tone: 'active',
        showBackToPlan,
        elapsedSince: runningStartedAt(signals.activitySnapshot),
        debugSource: 'activity:running',
      };
    }
    const blocked = hasFreshPhaseBProgressRecord(signals.phaseProgress, signals.nowMs)
      ? blockedBuildAttention(signals, freshRunningActivity, showBackToPlan)
      : null;
    if (blocked) return blocked;
    const phaseBProgressState = hasFreshPhaseBProgress(signals.phaseProgress, signals.nowMs)
      ? phaseProgressRunState(signals, showBackToPlan)
      : null;
    if (phaseBProgressState) return phaseBProgressState;
    if (signals.provenLive === true) {
      return {
        kind: 'building',
        label: 'Agent working',
        detail: 'Live build progress is still arriving.',
        tone: 'active',
        showBackToPlan,
        elapsedSince: runningStartedAt(signals.activitySnapshot),
        debugSource: 'activity:poll-proven-live',
      };
    }
    return {
      kind: 'ready',
      label: 'Ready',
      detail: 'Foundry found legacy build output, but this run predates completion verification.',
      tone: 'neutral',
      legacyCompletionUnverified: true,
      showBackToPlan: false,
      debugSource: 'interview-status:legacy-completion-unverified',
    };
  }

  if (ATTENTION_STATUSES.has(interview.status as BuildGameInterviewStatus)) {
    return {
      kind: 'needs-attention',
      label: 'Build needs attention',
      detail: 'The build-game flow was abandoned.',
      tone: 'warning',
      primaryCta: 'retry',
      showBackToPlan,
      debugSource: `interview-status:${interview.status}`,
    };
  }

  if (interview.status && TERMINAL_STATUSES.has(interview.status)) {
    return unverifiedBuildCompletionState({
      showBackToPlan,
      debugSource: `interview-status:${interview.status}:completion-unverified`,
    });
  }

  if (
    interview.pendingPhaseBDecision &&
    shouldSurfacePendingPhaseBDecision({
      activitySnapshot: signals.activitySnapshot,
      phaseProgress: signals.phaseProgress,
    })
  ) {
    return {
      kind: 'waiting-on-user',
      label: 'Waiting on you',
      detail: phaseBDecisionDetail(interview.pendingPhaseBDecision),
      tone: 'waiting',
      primaryCta: 'back-to-plan',
      showBackToPlan,
      elapsedSince: parseIsoTimeMs(interview.pendingPhaseBDecision.askedAt),
      debugSource: `interview-state:phase-b-decision:${interview.pendingPhaseBDecision.category ?? 'unknown'}`,
    };
  }

  // A persisted `in-progress` state is not itself proof that an interview is
  // alive. Historical Cloud runs can survive a renderer/runtime failure with
  // no pending question and no agent to advance them. Once the canonical
  // progress timestamp crosses the existing five-minute noisy-stuck threshold,
  // stop claiming "Working" and offer a non-destructive resume. Real question
  // surfaces, pending decisions, and observed agent activity all win above.
  if (staleInterviewAt !== null) {
    return {
      kind: 'needs-attention',
      label: 'Interview stalled',
      detail: 'No question or build agent has checked in for this interview for a while.',
      tone: 'warning',
      primaryCta: 'retry',
      showBackToPlan: false,
      elapsedSince: staleInterviewAt,
      debugSource: 'interview-status:in-progress:stale',
    };
  }

  const buildDispatched = hasBuildDispatchEvidence(interview);
  const staleFinalReview = buildDispatched
    ? staleFinalReviewAttentionFromLegacy({
        signals,
        interview,
        freshRunningActivity,
        showBackToPlan,
      })
    : null;
  if (staleFinalReview) return staleFinalReview;

  const staleBuild = buildDispatched
    ? staleBuildAttentionFromPhaseProgress({
        signals,
        freshRunningActivity,
        showBackToPlan,
        nowMs: signals.nowMs,
      })
    : null;
  if (staleBuild) return staleBuild;

  const handoffPending = buildHandoffPendingRunState({
    signals,
    interview,
    freshRunningActivity,
  });
  if (handoffPending) return handoffPending;

  if (interview.depth === 'quick') {
    const hasPlan = interview.plan.trim().length > 0;
    if (interview.subPhase === 'committed') {
      const watchdog = signals.buildKickoffWatchdog;
      const phaseBActivityStarted =
        freshRunningActivity || phaseProgressBeyondPlanReview(signals.phaseProgress);
      if (
        watchdog &&
        !phaseBActivityStarted &&
        (watchdog.status === 'stalled' || watchdog.status === 'resume-attempted')
      ) {
        return {
          kind: 'needs-attention',
          label: 'Start building stalled',
          detail: 'The plan was approved, but Phase B did not start.',
          tone: 'warning',
          primaryCta: 'retry',
          showBackToPlan: true,
          elapsedSince: watchdog.stalledAt ?? watchdog.committedAt ?? null,
          debugSource: `interview-state:quick:committed:watchdog-${watchdog.status}`,
        };
      }
      if (phaseProgressBeyondPlanReview(signals.phaseProgress)) {
        return {
          kind: 'building',
          label: 'Building',
          detail: 'Phase B has started.',
          tone: 'active',
          primaryCta: 'back-to-plan',
          showBackToPlan: true,
          elapsedSince: runningStartedAt(signals.activitySnapshot),
          debugSource: 'phase-progress:build-after-committed',
        };
      }
      if (hasFreshBuildAgentActivity(signals.activitySnapshot)) {
        return {
          kind: 'building',
          label: 'Building',
          detail: 'Build agents are working through implementation.',
          tone: 'active',
          primaryCta: 'back-to-plan',
          showBackToPlan: true,
          elapsedSince: runningStartedAt(signals.activitySnapshot),
          debugSource: 'activity:build-agent-after-committed',
        };
      }
      return {
        kind: 'build-kickoff-pending',
        label: 'Build kickoff pending',
        detail: 'Waiting for Phase B to start.',
        tone: 'active',
        primaryCta: 'back-to-plan',
        showBackToPlan: true,
        elapsedSince: watchdog?.committedAt ?? runningStartedAt(signals.activitySnapshot),
        debugSource:
          watchdog?.status === 'pending'
            ? 'interview-state:quick:committed:watchdog-pending'
            : 'interview-state:quick:committed',
      };
    }
    if (interview.subPhase === 'build') {
      const blocked = blockedBuildAttention(signals, freshRunningActivity, true);
      if (blocked) return blocked;
      return {
        kind: 'building',
        label: 'Building',
        detail: 'The agent is working through the implementation.',
        tone: 'active',
        primaryCta: 'back-to-plan',
        showBackToPlan: true,
        elapsedSince: runningStartedAt(signals.activitySnapshot),
        debugSource: 'interview-state:quick:build',
      };
    }
    if (
      interview.subPhase === 'plan' ||
      interview.subPhase === 'refine' ||
      interview.subPhase === 'back-to-plan'
    ) {
      if (hasPlan) {
        return {
          kind: 'waiting-for-plan-review',
          label: 'Review Build Plan',
          detail: 'Review the plan, refine it, or start building.',
          tone: 'waiting',
          primaryCta: 'start-building',
          showBackToPlan: false,
          elapsedSince: runningStartedAt(signals.activitySnapshot),
          debugSource: `interview-state:quick:${interview.subPhase}:plan-present`,
        };
      }
      // crucible#3183 — the design agent already reached a TERMINAL 'failed'
      // status and no plan is coming (see the field's own doc comment on
      // WorkspaceRunSignals). Without this check, "drafting-plan" below runs
      // forever: nothing else in this branch's condition ever changes once
      // the design agent has already given up.
      //
      // Deliberately OMITS `primaryCta: 'retry'` (unlike the fastPlanMissingAt
      // sibling case a few lines below): that CTA wires to `handleRetryAutoBuild`
      // → `build:autoPipeline:retry` → `AutoBuildOrchestrator.retry`, which
      // retries an ALREADY-DISPATCHED build pipeline — there is no such
      // pipeline here (the failure happened upstream of any build dispatch, so
      // there is nothing for that action to retry). Matches the no-CTA
      // precedent `blockedBuildAttention` above uses for the same reason (an
      // action with no correct handler is worse than none — a dead button
      // reads as "this should work" when it can't). This state's job is just
      // to stop the silent infinite spinner; a correct retry-the-design-step
      // affordance is follow-up work, not bundled into this fix.
      if (signals.designSystemFailedAt != null) {
        return {
          kind: 'needs-attention',
          label: 'Build plan needs attention',
          detail: "Hit a hiccup generating the game's art direction, so no build plan was saved.",
          tone: 'warning',
          showBackToPlan: false,
          elapsedSince: signals.designSystemFailedAt,
          debugSource: `interview-state:quick:${interview.subPhase}:design-system-failed`,
        };
      }
      return {
        kind: 'drafting-plan',
        label: 'Drafting plan',
        detail: 'The agent is turning your answers into a build plan.',
        tone: 'active',
        showBackToPlan: false,
        elapsedSince: runningStartedAt(signals.activitySnapshot),
        debugSource: `interview-state:quick:${interview.subPhase}:plan-empty`,
      };
    }
    if (interview.subPhase === 'quick') {
      if (hasPlan) {
        return {
          kind: 'waiting-for-plan-review',
          label: 'Review Build Plan',
          detail: 'Review the plan, refine it, or start building.',
          tone: 'waiting',
          primaryCta: 'start-building',
          showBackToPlan: false,
          elapsedSince: runningStartedAt(signals.activitySnapshot),
          debugSource: 'interview-state:quick:answers-submitted:plan-present',
        };
      }
      const fastAnswersReady =
        hasPlan === false &&
        (fastAnswersSubmitted(signals.interviewState) || signals.fastAnswersSubmittedAt != null);
      if (signals.fastPlanMissingAt != null && fastAnswersReady) {
        return {
          kind: 'needs-attention',
          label: 'Build plan needs attention',
          detail: 'Fast answers were submitted, but no build plan was saved.',
          tone: 'warning',
          primaryCta: 'retry',
          showBackToPlan: false,
          elapsedSince: signals.fastPlanMissingAt,
          debugSource: 'interview-state:quick:answers-submitted:plan-missing',
        };
      }
      if (signals.designSystemFailedAt != null) {
        return {
          kind: 'needs-attention',
          label: 'Build plan needs attention',
          detail: "We couldn't prepare the build plan. Your interview answers are saved.",
          tone: 'warning',
          showBackToPlan: false,
          elapsedSince: signals.designSystemFailedAt,
          debugSource: 'interview-state:quick:answers-submitted:design-system-failed',
        };
      }
      if (fastAnswersReady) {
        return {
          kind: 'drafting-plan',
          label: 'Preparing Build Plan',
          detail: 'Turning your Fast answers into a build plan.',
          tone: 'active',
          showBackToPlan: false,
          elapsedSince:
            signals.fastAnswersSubmittedAt ?? runningStartedAt(signals.activitySnapshot),
          debugSource: fastAnswersSubmitted(signals.interviewState)
            ? 'interview-state:quick:answers-submitted'
            : 'renderer:fast-answers-submitted',
        };
      }
      return {
        kind: 'interview-in-progress',
        label: 'Interview in progress',
        detail: 'Answer the quick build-game questions.',
        tone: 'active',
        showBackToPlan: false,
        debugSource: 'interview-state:quick:quick',
      };
    }
  }

  if (interview.status === 'dispatched') {
    return {
      kind: 'build-kickoff-pending',
      label: 'Build kickoff pending',
      detail: 'Interview complete; waiting for implementation to start.',
      tone: 'active',
      showBackToPlan,
      debugSource: 'interview-status:dispatched',
    };
  }

  const blocked = blockedBuildAttention(signals, freshRunningActivity, showBackToPlan);
  if (blocked) return blocked;

  if (interview.status === 'building') {
    return {
      kind: 'building',
      label: 'Building',
      detail: 'The agent is working through implementation.',
      tone: 'active',
      showBackToPlan,
      elapsedSince: runningStartedAt(signals.activitySnapshot),
      debugSource: 'interview-status:building',
    };
  }

  if (interview.status === 'in-progress' || signals.isAiTyping) {
    return {
      kind: 'interview-in-progress',
      label: 'Interview in progress',
      detail: 'The agent is asking questions or processing your answer.',
      tone: 'active',
      showBackToPlan,
      elapsedSince: runningStartedAt(signals.activitySnapshot),
      debugSource:
        interview.status === 'in-progress' ? 'interview-status:in-progress' : 'chat:is-ai-typing',
    };
  }

  if (hasRunningActivity(signals.activitySnapshot)) {
    return {
      kind: 'building',
      label: 'Agent working',
      detail: 'Live activity is in progress.',
      tone: 'active',
      showBackToPlan,
      elapsedSince: runningStartedAt(signals.activitySnapshot),
      debugSource: 'activity:running',
    };
  }

  const progressState = phaseProgressRunState(signals, showBackToPlan);
  if (progressState) return progressState;

  return {
    kind: 'ready',
    label: 'Ready',
    tone: 'neutral',
    showBackToPlan: false,
    debugSource: 'default:ready',
  };
}
